
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import AdminHeader from './components/AdminHeader';
import ImageUpload from '../components/ImageUpload';

// API configuration
const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' ? 'https://fciveria.ge' : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const PhotoForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',
    category: 'მატჩები',
    isPublished: true,
    uploadDate: new Date().toISOString().split('T')[0]
  });

  const categories = ['მატჩები', 'ტრენინგი', 'ღონისძიებები', 'ფეხბურთელები', 'გულშემატკივრები', 'სტადიონი'];

  useEffect(() => {
    if (isEdit) {
      loadPhoto();
    }
  }, [id, isEdit]);

  const loadPhoto = async () => {
    try {
      // Get token from the same source as apiService
      const storedUser = localStorage.getItem('fciveria_admin_user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      if (!token) {
        console.error('No auth token found');
        return;
      }
      
      const response = await fetch(getApiUrl(`/api/photos/${id}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const photo = await response.json();
        setFormData({
          title: photo.title,
          description: photo.description || '',
          imageUrl: photo.imageUrl,
          category: photo.category,
          isPublished: photo.isPublished,
          uploadDate: photo.uploadDate ? photo.uploadDate.split('T')[0] : new Date().toISOString().split('T')[0]
        });
      } else {
        console.error('Error loading photo:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading photo:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get token from the same source as apiService
      const storedUser = localStorage.getItem('fciveria_admin_user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      if (!token) {
        console.error('No auth token found');
        setLoading(false);
        return;
      }
      
      const url = isEdit 
        ? getApiUrl(`/api/photos/${id}`)
        : getApiUrl('/api/photos');
      
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        navigate('/_panel-7c39f8/photos');
      } else {
        const error = await response.json();
        console.error('Error saving photo:', error);
        alert('შეცდომა ფოტოს შენახვისას');
      }
    } catch (error) {
      console.error('Error saving photo:', error);
      alert('შეცდომა ფოტოს შენახვისას');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader 
        title={isEdit ? 'ფოტოს რედაქტირება' : 'ახალი ფოტო'}
        subtitle={isEdit ? 'შეცვალეთ ფოტოს ინფორმაცია' : 'დაამატეთ ახალი ფოტო გალერეაში'}
      >
        <button
          onClick={() => navigate('/_panel-7c39f8/photos')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          უკან
        </button>
      </AdminHeader>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-6 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 font-georgian">
                    სათაური *
                  </label>
                  <input
                    type="text"
                    name="title"
                    id="title"
                    required
                    value={formData.title}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    placeholder="შეიყვანეთ ფოტოს სათაური"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 font-georgian">
                    აღწერა
                  </label>
                  <textarea
                    name="description"
                    id="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    placeholder="შეიყვანეთ ფოტოს აღწერა"
                  />
                </div>

                <ImageUpload
                  value={formData.imageUrl}
                  onChange={(url) => setFormData(prev => ({ ...prev, imageUrl: url }))}
                  category="photos"
                  label="ფოტო *"
                  required={true}
                />

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 font-georgian">
                    კატეგორია *
                  </label>
                  <select
                    name="category"
                    id="category"
                    required
                    value={formData.category}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                  >
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">

                <div>
                  <label htmlFor="uploadDate" className="block text-sm font-medium text-gray-700 font-georgian">
                    ატვირთვის თარიღი
                  </label>
                  <input
                    type="date"
                    name="uploadDate"
                    id="uploadDate"
                    value={formData.uploadDate}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    id="isPublished"
                    name="isPublished"
                    type="checkbox"
                    checked={formData.isPublished}
                    onChange={handleChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-900 font-georgian">
                    გამოქვეყნებული
                  </label>
                </div>

              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/_panel-7c39f8/photos')}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
              >
                გაუქმება
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 font-georgian"
              >
                {loading ? 'ინახება...' : (isEdit ? 'განახლება' : 'შენახვა')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PhotoForm;
